import { useCallback, useMemo, useState } from 'react';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { BrowserRouter } from 'react-router';
import { ApiClient } from './api/client';
import { apiScopes, loginRequest, popupRedirectUri } from './auth/authConfig';
import { createMsalTokenProvider } from './auth/msalTokenProvider';
import { HubProvider } from './shell/HubContext';
import { AppRoutes } from './shell/AppRoutes';
import { MedullaLogo } from './shell/Logo';

/** Entry point for the normal, Entra-authenticated path. */
export function App() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  // Sign-in failures used to go nowhere: the call was fired with `void` and no catch, so a blocked
  // popup or a rejected request left the button looking like it had done nothing at all.
  const [signInError, setSignInError] = useState<string | null>(null);

  const api = useMemo(
    () => (account ? new ApiClient(createMsalTokenProvider(instance, account.homeAccountId)) : null),
    [instance, account],
  );

  /**
   * Asks for the extra Graph scopes the API needs on the user's behalf. Reached when the tenant has
   * not consented org-wide, so each user grants it once.
   */
  const handleConsentRequired = useCallback(
    (scopes: string[]) => {
      // A popup rather than a redirect: consent is asked for mid-task, often with a form already
      // filled in, and a redirect would throw that away. It returns to the blank auth-redirect page
      // so the popup does not load the app a second time.
      instance
        .acquireTokenPopup({
          scopes: scopes.length > 0 ? scopes : apiScopes,
          account,
          redirectUri: popupRedirectUri,
        })
        .catch((error: unknown) => {
          console.error('Consent request failed', error);
        });
    },
    [instance, account],
  );

  return (
    <>
      <UnauthenticatedTemplate>
        <header className="topbar">
          <div className="topbar__brand">
            <MedullaLogo />
            <span className="topbar__name">HR Hub</span>
          </div>
        </header>

        <main>
          <div className="signin">
            <h1 className="signin__title">Welcome to HR Hub</h1>
            <p className="signin__body">Please sign in with your work account to continue.</p>
            <button
              type="button"
              className="button button--primary"
              onClick={() => {
                setSignInError(null);

                // Navigates this tab to Entra and back. main.tsx consumes the response with
                // handleRedirectPromise() before the first render, so nothing is needed here for the
                // return leg — only a catch, for the case where the redirect never starts.
                instance.loginRedirect(loginRequest).catch((error: unknown) => {
                  console.error('Sign-in failed', error);
                  setSignInError(
                    error instanceof Error ? error.message : 'Sign-in could not be started.',
                  );
                });
              }}
            >
              Sign in with your work account
            </button>

            {signInError && (
              <p className="signin__error" role="alert">
                {signInError}
              </p>
            )}
          </div>
        </main>
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        {api ? (
          <HubProvider
            value={{
              api,
              userLabel: account?.name ?? account?.username ?? '',
              onSignOut: () => void instance.logoutRedirect(),
              draftUnavailableReason: null,
              onConsentRequired: handleConsentRequired,
            }}
          >
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </HubProvider>
        ) : (
          <div className="page-message">Preparing your session…</div>
        )}
      </AuthenticatedTemplate>
    </>
  );
}
